// <s-bundle> keeps the bundle total, item count and submitted cart items in sync with the
// tile checkboxes. Unticked tiles disable their hidden inputs so the native form only posts
// ticked items. Without JS every tile is submitted, which matches the default ticked state.
class SBundle extends HTMLElement {
  connectedCallback() {
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('submit', this.handleSubmit);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('submit', this.handleSubmit);
  }

  // Adds the ticked items without leaving the page, refreshes every cart view and drops
  // the mini cart from the header icon. Falls back to the normal form post on failure.
  async handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || this.busy) return;
    event.preventDefault();
    this.busy = true;
    const swipe = this.querySelector('s-swipe-submit');
    try {
      const response = await fetch(`${form.getAttribute('action').replace(/\/$/, '')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
      document.dispatchEvent(new CustomEvent('s-cart-peek:open'));
      swipe?.reset?.();
    } catch (error) {
      console.error(error);
      form.submit();
    } finally {
      this.busy = false;
    }
  }

  handleChange(event) {
    if (event.target.matches('[data-bundle-checkbox]')) this.update();
  }

  update() {
    const checkboxes = this.querySelectorAll('[data-bundle-checkbox]');
    let total = 0;
    let count = 0;
    checkboxes.forEach((checkbox) => {
      const included = checkbox.checked;
      const tile = checkbox.closest('li');
      tile?.querySelectorAll('[data-bundle-input]').forEach((input) => {
        input.disabled = !included;
      });
      if (included) {
        total += Number(checkbox.dataset.price || 0);
        count += 1;
      }
    });

    const totalEl = this.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(total);

    const countEl = this.querySelector('[data-bundle-count]');
    if (countEl?.dataset.labelTemplate) {
      countEl.textContent = countEl.dataset.labelTemplate.replace('__COUNT__', String(count));
    }

    const submit = this.querySelector('[data-bundle-submit]');
    if (submit) submit.disabled = count === 0;
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const group = (value, decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(cents, 0, '.', ',');
        default:
          return group(cents, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-bundle')) customElements.define('s-bundle', SBundle);
